import { withBxApiContext } from 'helpers/withBxApiContext';
import bxApi from 'services/bxApi';
import graphql from 'services/bxApi/graphql';

import { ROUTE_TYPES } from 'app/(clickouts)/constants';
import { transformBxApiProductsForExplore } from 'helpers/dataTransformers/homeExploreData';
import { generateHomePageHead } from 'helpers/dataTransformers/homePageHead';
import { getAppData, setCacheTags } from 'helpers/initializePage';
import Home from 'screens/home';
import { contentfulResourcesService } from 'services/contentful';
import initializeExperiments from 'services/launchDarkly';

const DEFAULT_POPULAR_CATEGORY_SLUG = 'crm';
const VISIBLE_HOME_CAROUSEL_TYPES = new Set(['HomeLearn']);

const POPULAR_CATEGORY_SLUGS = [
  'crm',
  'marketing-automation',
  'email-marketing',
  'business-intelligence',
  'social-media',
  'survey',
  'data-visualization',
  'data-analysis',
  'content-marketing',
  'lead-generation',
  'customer-loyalty',
  'web-analytics',
  'project-management',
  'inventory-management',
  'scheduling',
  'appointments-scheduling',
  'human-resources',
  'billing-invoicing',
  'time-tracking',
  'accounting',
  'task-management',
  'applicant-tracking',
  'field-service-management',
  'membership-management',
  'time-clock-attendance',
  'help-desk-ticketing',
  'reservation-online-booking',
  'construction-management',
  'live-chat',
  'warehouse-management',
];

const fetchHomeCategories = async (countryCode, popularCategorySlug) => {
  const slugsToFetch = [
    ...new Set([...POPULAR_CATEGORY_SLUGS, popularCategorySlug]),
  ];

  const results = await Promise.all(
    slugsToFetch.map((slug) =>
      bxApi.getHomeCategoryBySlug(countryCode, slug).catch(() => null)
    )
  );

  const bySlug = Object.fromEntries(
    results.filter(Boolean).map((node) => [node.slug, node])
  );

  const popularCategory =
    bySlug[popularCategorySlug] ??
    bySlug[DEFAULT_POPULAR_CATEGORY_SLUG] ??
    null;

  const categories = POPULAR_CATEGORY_SLUGS.map((slug) => bySlug[slug]).filter(
    Boolean
  );

  return { popularCategory, categories };
};

const SORT_MAP = {
  reviews: 'MOST_REVIEWS',
  ease_of_use: 'MOST_REVIEWS', // Fetch most reviewed products, then sort client-side by ease of use
  value_for_money: 'MOST_REVIEWS', // Fetch most reviewed products, then sort client-side by value for money
};

const BATCH_SIZE_FOR_CLIENT_SORT = 20;
const FINAL_LISTING_COUNT = 6;

const fetchPopularCategoryListings = async (
  countryCode,
  categorySlug,
  sort = 'reviews'
) => {
  try {
    const bxApiSort = SORT_MAP[sort] || 'MOST_REVIEWS';
    const needsClientSideSort =
      sort === 'ease_of_use' || sort === 'value_for_money';
    const batchSize = needsClientSideSort
      ? BATCH_SIZE_FOR_CLIENT_SORT
      : FINAL_LISTING_COUNT;

    const result = await graphql.fetchDirectoryCategoryProducts({
      slug: categorySlug,
      countryCode,
      batchSize,
      sort: [bxApiSort],
    });

    let products = result.softwareCategoryBySlug?.products?.nodes || [];

    // Client-side sorting for ease of use and value for money
    if (needsClientSideSort && products.length > 0) {
      products = [...products]; // Clone to avoid mutation

      if (sort === 'ease_of_use') {
        products.sort((a, b) => {
          const ratingA = a.reviewSummary?.easeOfUseRating || 0;
          const ratingB = b.reviewSummary?.easeOfUseRating || 0;
          return ratingB - ratingA;
        });
      } else if (sort === 'value_for_money') {
        products.sort((a, b) => {
          const ratingA = a.reviewSummary?.valueForMoneyRating || 0;
          const ratingB = b.reviewSummary?.valueForMoneyRating || 0;
          return ratingB - ratingA;
        });
      }

      products = products.slice(0, FINAL_LISTING_COUNT);
    }

    return transformBxApiProductsForExplore(products, categorySlug);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching popular category listings:', error);
    return [];
  }
};

export const getServerSideProps = withBxApiContext('home', async (ctx) => {
  // Redirect if user is logged in and open_login param is present
  // (previously handled in backend: redirect_without_open_login_param! if social_login? && open_login_param?)
  const isLoggedIn = !!ctx.req.session?.community_user_id;
  const hasOpenLoginParam = ctx.query.open_login === '1';

  if (isLoggedIn && hasOpenLoginParam) {
    const queryWithoutOpenLogin = { ...ctx.query };
    delete queryWithoutOpenLogin.open_login;

    const queryString = new URLSearchParams(queryWithoutOpenLogin).toString();
    const redirectPath = queryString ? `/?${queryString}` : '/';

    return {
      redirect: {
        destination: redirectPath,
        permanent: false,
      },
    };
  }

  const countryCode =
    ctx.query.geoip || ctx.req.headers['cf-ipcountry'] || 'US';
  const popularCategorySlug =
    ctx.req.cookies?.seen_popular_category || DEFAULT_POPULAR_CATEGORY_SLUG;
  const selectedSort = ctx.req.cookies?.homepage_explore_sort || 'reviews';
  const contentfulService = contentfulResourcesService({
    preview: ctx.query.preview,
    environment: ctx.req?.headers['fe-contentful-env'],
  });
  const [hreflangs, experiments, posts, homeData, homeCategories] =
    await Promise.all([
      bxApi.getHomepageHreflangs().catch(() => []),
      initializeExperiments({
        ctx,
        pageType: 'static',
      }),
      contentfulService.getHomePost().catch(() => null),
      contentfulService.getHomeContent().catch(() => null),
      fetchHomeCategories(countryCode, popularCategorySlug).catch(() => ({
        popularCategory: null,
        categories: [],
      })),
    ]);

  const homeContent = {
    categoryHubsCarousel:
      homeData?.carouselCollection?.items?.filter((item) =>
        VISIBLE_HOME_CAROUSEL_TYPES.has(item.type)
      ) || [],
  };

  // Fetch listings for the popular category from BX-API
  const listings = homeCategories.popularCategory
    ? await fetchPopularCategoryListings(
        countryCode,
        homeCategories.popularCategory.slug,
        selectedSort
      )
    : [];

  // Generate head metadata (previously from backend /api/home)
  const headData = generateHomePageHead(ctx);
  const pageData = {
    header: {},
    head: headData,
    route: ROUTE_TYPES.HOME,
    google_analytics: {
      content_group: ROUTE_TYPES.HOME,
    },
  };

  const appData = {
    ...getAppData(ctx, pageData),
    experiments,
    hreflangs,
    isForcingOpenLoginModal: ctx.query.open_login === '1',
  };

  appData.earlyHintsCss = true;
  ctx.req.APP_DATA = appData;
  setCacheTags(ctx, 'static');

  return {
    props: {
      posts,
      homeContent,
      appData,
      popularCategories: homeCategories.categories,
      popularCategory: homeCategories.popularCategory
        ? { ...homeCategories.popularCategory, listings }
        : null,
    },
  };
});

export default Home;
